'use client';

import { ServerActionName } from '@headless-workspace/config';
import { CacheKeys } from '@headless-workspace/config';
import { ResultServerActionType } from '@headless-workspace/core-domain';
import { NumberHelpers } from '@headless-workspace/utils';
import { TriggerWithArgs } from 'swr/mutation';
import {
    BasketProductQuantityValue,
    CustomerBasketValue,
    EngraveFormReturnType,
    updateBasketServerAction,
    useSafeSWRMutation,
} from '../../../../src';

type AddToBasketArgs = {
    id: string;
    quantity?: number;
    engrave?: EngraveFormReturnType;
}[];

type MutationTriggerReturnType = TriggerWithArgs<
    ResultServerActionType<CustomerBasketValue>,
    unknown,
    typeof CacheKeys.Basket,
    AddToBasketArgs
>;

type UseAddToBasketReturnType = {
    add: MutationTriggerReturnType;
    isMutating: boolean;
};

const DEFAULT_QUANTITY = 1;

export const useBasketMutations = (): UseAddToBasketReturnType => {
    const { trigger: additionTrigger, isMutating } = useSafeSWRMutation<CustomerBasketValue, AddToBasketArgs>(
        CacheKeys.Basket,
        ServerActionName.updateBasket,
        async (_, { arg }) => {
            const basketUpdatePayload = arg.reduce<BasketProductQuantityValue[]>((acc, curr) => {
                const sku = NumberHelpers.parseNumber(curr.id);
                if (sku) {
                    const engrave: EngraveFormReturnType | undefined =
                        curr.engrave && curr.engrave.font && curr.engrave.line1 ? curr.engrave : undefined;

                    acc.push({
                        id: sku,
                        quantity: curr.quantity ?? DEFAULT_QUANTITY,
                        engrave,
                    });
                }
                return acc;
            }, []);

            return await updateBasketServerAction(basketUpdatePayload);
        },
    );

    return {
        add: additionTrigger,
        isMutating,
    };
};
